import React, { useState, useEffect, useRef } from 'react';
import TextInput from './inputs/TextInput';
import VoiceInput from './inputs/VoiceInput';
import TestTimer from '../../ui/TestTimer';
import { Settings, PracticeResults } from '../types';
import { Question } from '../../../types';
import { useAuth } from '../../../context/AuthContext';
import { updateProgressAssessment, completeProgressAssessment, updateTypingSpeed } from '../../../config/api';
import { logError } from '../../../utils/errorReporting';
import { progressQueue } from '../../../services/ProgressQueueService';
import { useVoiceAssessment } from './hooks/useVoiceAssessment';

interface QuestionScreenProps {
  onEnd: (results: PracticeResults) => void;
  settings: Settings;
  questions: Question[];
}

const QuestionScreen: React.FC<QuestionScreenProps> = ({ onEnd, settings, questions: initialQuestions }) => {
  // All state hooks first
  const [currentIndex, setCurrentIndex] = useState(0);
  const [questionsAnswered, setQuestionsAnswered] = useState(0);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [showFeedback, setShowFeedback] = useState(false);
  const [isProcessingEnd, setIsProcessingEnd] = useState(false);
  const [questionStartTime, setQuestionStartTime] = useState<number>(Date.now());
  const isSubmitting = useRef<boolean>(false);
  const { user } = useAuth();

  // Add test start time ref
  const testStartTime = useRef<number>(Date.now());

  // Keep refs for non-voice inputs
  const questionsAnsweredRef = useRef(questionsAnswered);
  const correctAnswersRef = useRef(correctAnswers);

  // Add ref for storing typing data
  const typingDataQueue = useRef<Array<{ count: number; time: number }>>([]);

  // Add ref to track progress update promises
  const progressUpdatePromises = useRef<Promise<any>[]>([]);
  const isMounted = useRef(true);

  const totalTypingTimeDeducted = useRef<number>(0);
  const totalTransitionTime = useRef<number>(0);
  const lastTransitionStartTime = useRef<number>(0);

  const isVoiceMode = settings.inputMethod === 'voice';
  const voiceAdvancingRef = useRef(false);

  const voiceAssessment = useVoiceAssessment(isVoiceMode, user?.userId || '', settings.assessmentId);

  // Cleanup effect
  useEffect(() => {
    return () => {
      isMounted.current = false;
      // Process any pending updates before unmounting
      if (progressUpdatePromises.current.length > 0) {
        Promise.all(progressUpdatePromises.current).catch(error => {
          logError(error, {
            component: 'ProgressAssessment-QuestionScreen',
            userId: user?.userId
          });
        });
      }
    };
  }, []);

  useEffect(() => {
    questionsAnsweredRef.current = questionsAnswered;
  }, [questionsAnswered]);

  useEffect(() => {
    correctAnswersRef.current = correctAnswers;
  }, [correctAnswers]);

  // Voice mode: initialize microphone on mount
  useEffect(() => {
    if (!isVoiceMode) return;

    const initMic = async () => {
      const success = await voiceAssessment.initializeMicrophone();
      if (success) {
        // Reset test start time so mic permission prompt doesn't burn assessment time
        testStartTime.current = Date.now();
        setQuestionStartTime(Date.now());
      }
    };
    initMic();

    return () => {
      voiceAssessment.releaseMicrophone();
    };
  }, [isVoiceMode]);

  const handlePracticeEnd = async () => {
    try {
      if (isProcessingEnd) return;
      setIsProcessingEnd(true);

      if (!user?.userId) {
        throw new Error('No authenticated user found');
      }

      if (isVoiceMode) {
        // Finalize voice results — waits for all pending transcriptions
        // Pass current question so the last clip (answer spoken but space not pressed) gets scored
        const currentQ = initialQuestions[currentIndex];
        const voiceResults = await voiceAssessment.finalizeResults(
          currentQ ? { id: currentQ.id, answer: currentQ.answer } : undefined,
          questionStartTime
        );
        voiceAssessment.releaseMicrophone();

        const testDurationMs = Date.now() - testStartTime.current;
        const testDurationMinutes = testDurationMs / 60000;

        const MAX_ATTEMPTS = 5;
        let attempts = 0;
        let assessmentResult: any = null;

        while (attempts < MAX_ATTEMPTS && !assessmentResult) {
          try {
            assessmentResult = await completeProgressAssessment(settings.assessmentId, user.userId, {
              totalTypingTimeDeducted: 0,
              totalTransitionTime: 0,
              actualDurationMinutes: testDurationMinutes,
              clientSideStats: {
                totalAttempts: voiceResults.totalAttempted,
                totalCorrect: voiceResults.totalCorrect
              },
              testType: 'TotalTimer'
            });
          } catch (error) {
            attempts++;
            logError(error, {
              component: 'ProgressAssessment-QuestionScreen-VoiceRetry',
              userId: user?.userId,
              attempt: attempts
            });
            if (attempts < MAX_ATTEMPTS) {
              await new Promise(res => setTimeout(res, attempts * 1000));
            }
          }
        }

        if (assessmentResult) {
          const results: PracticeResults = {
            cqpm: Math.round(assessmentResult.overallCQPM),
            accuracy: assessmentResult.accuracyRate,
            totalQuestions: initialQuestions.length,
            totalAttempted: voiceResults.totalAttempted,
            correctAnswers: voiceResults.totalCorrect
          };
          setIsProcessingEnd(false);
          onEnd(results);
        } else {
          logError(new Error('Failed to complete voice assessment after max retries'), {
            component: 'ProgressAssessment-QuestionScreen-Voice',
            userId: user?.userId,
            attempts: MAX_ATTEMPTS
          });
          const results: PracticeResults = {
            cqpm: 0,
            totalQuestions: initialQuestions.length,
            totalAttempted: voiceResults.totalAttempted,
            correctAnswers: voiceResults.totalCorrect,
            accuracy: 0
          };
          setIsProcessingEnd(false);
          onEnd(results);
        }
        return;
      }

      // We don't need to wait for progress updates to complete
      // since we're using the queue system now
      // The queue will handle retries and persistence

      // Process all typing data first
      if (typingDataQueue.current.length > 0) {
        try {
          const totalCount = typingDataQueue.current.reduce((sum, data) => sum + data.count, 0);
          const totalTime = typingDataQueue.current.reduce((sum, data) => sum + data.time, 0);

          if (totalCount > 0 && totalTime > 0) {
            // Use the queue for typing speed updates
            progressQueue.enqueueTypingUpdate(
              user.userId,
              {
                count: totalCount,
                time: totalTime / totalCount
              }
            );
          }
        } catch (error) {
          logError(error, {
            component: 'ProgressAssessment-QuestionScreen-TypingData',
            userId: user?.userId
          });
        }
      }

      const allAttemptedQuestions = initialQuestions.slice(0, questionsAnswered);

      // Calculate actual test duration in minutes
      const testDurationMs = Date.now() - testStartTime.current;
      const testDurationMinutes = testDurationMs / 60000; // Convert to minutes

      // Complete the assessment with additional timing data
      // Retry logic for completing the assessment
      const MAX_ATTEMPTS = 5;
      let attempts = 0;
      let assessmentResult: any = null;

      while (attempts < MAX_ATTEMPTS && !assessmentResult) {
        try {
          assessmentResult = await completeProgressAssessment(settings.assessmentId, user.userId, {
            totalTypingTimeDeducted: totalTypingTimeDeducted.current,
            totalTransitionTime: totalTransitionTime.current,
            actualDurationMinutes: testDurationMinutes,
            clientSideStats: {
              totalAttempts: questionsAnswered,
              totalCorrect: correctAnswers
            },
            testType: 'TotalTimer'
          });
        } catch (error) {
          attempts++;
          logError(error, {
            component: 'ProgressAssessment-QuestionScreen-Retry',
            userId: user?.userId,
            attempt: attempts
          });

          // If not the last attempt, wait a bit before retrying (simple backoff)
          if (attempts < MAX_ATTEMPTS) {
            await new Promise(res => setTimeout(res, attempts * 1000));
          }
        }
      }

      if (assessmentResult) {
        const results: PracticeResults = {
          cqpm: Math.round(assessmentResult.overallCQPM),
          accuracy: assessmentResult.accuracyRate,
          totalQuestions: initialQuestions.length,
          totalAttempted: questionsAnswered,
          correctAnswers: correctAnswers
        };

        setIsProcessingEnd(false);
        onEnd(results);
      } else {
        // After all retries have failed, log and show fallback results
        logError(new Error('Failed to complete assessment after max retries'), {
          component: 'ProgressAssessment-QuestionScreen',
          userId: user?.userId,
          attempts: MAX_ATTEMPTS
        });

        const results: PracticeResults = {
          cqpm: 0,
          totalQuestions: initialQuestions.length,
          totalAttempted: questionsAnswered,
          correctAnswers: correctAnswers,
          accuracy: 0
        };

        setIsProcessingEnd(false);
        onEnd(results);
      }
    } catch (error) {
      logError(error, {
        component: 'ProgressAssessment-QuestionScreen',
        userId: user?.userId
      });
      setIsProcessingEnd(false);
      alert('Error processing results. Please try again.');
    }
  };

  const handleAnswer = async (answer: number, typingData?: { count: number; time: number }) => {
    if (!initialQuestions[currentIndex]) {
      console.warn('No current question found');
      return;
    }

    const currentQuestion = initialQuestions[currentIndex];
    const isCorrect = answer === currentQuestion.answer;
    const totalTimeSpentMs = Date.now() - questionStartTime;
    
    // Calculate and subtract the extra time given for digits
    const digitCount = currentQuestion.answer.toString().length;
    const digitTimeMs = digitCount > 1
      ? (digitCount - 1) * settings.timePerDigit * 1000 // Only deduct for digits beyond the first one
      : 0; // No deduction for 1 digit answers
    const adjustedTimeSpentMs = Math.max(100, totalTimeSpentMs - digitTimeMs);
    
    // Track total typing time deducted
    totalTypingTimeDeducted.current += digitTimeMs;
    
    // Start tracking transition time
    lastTransitionStartTime.current = Date.now();
    
    setShowFeedback(true);
    setTimeout(() => {
      // Calculate and add transition time
      const transitionEndTime = Date.now();
      totalTransitionTime.current += transitionEndTime - lastTransitionStartTime.current;
      
      setShowFeedback(false);
      setCurrentIndex(currentIndex + 1);
      setQuestionStartTime(Date.now());
    }, 100);

    // Queue typing data for batch processing if available
    if (typingData && isCorrect) {
      typingDataQueue.current.push(typingData);
    }

    // Increment questions answered counter
    const newQuestionsAnswered = questionsAnswered + 1;
    const newCorrectAnswers = isCorrect ? correctAnswers + 1 : correctAnswers;
    setQuestionsAnswered(newQuestionsAnswered);

    if (isCorrect) {
      setCorrectAnswers(newCorrectAnswers);
    }

    if (user?.userId) {
      try {
        const factId = currentQuestion.id.split('FACT#')[1];
        if (!factId) {
          throw new Error('Invalid fact ID format');
        }

        // 1. Use progress queue for assessment updates instead of direct API call
        progressQueue.enqueueProgressAssessmentUpdate(
          user.userId,
          settings.assessmentId,
          {
            [currentQuestion.id]: {
              attempts: 1,
              correct: isCorrect ? 1 : 0,
              timeSpent: adjustedTimeSpentMs
            }
          }
        );
        
        // 2. Update user progress using queue
        progressQueue.enqueueProgressUpdate(
          user.userId,
          {
            facts: {
              [factId]: {
                attempts: 1,
                correct: isCorrect ? 1 : 0,
                timeSpent: adjustedTimeSpentMs,
                practiceContext: 'progressAssessment'
              }
            }
          }
        );
        


      } catch (error) {
        logError(error, {
          component: 'ProgressAssessment-QuestionScreen-UpdateProgress',
          userId: user?.userId,
          factId: currentQuestion?.id,
          assessmentId: settings.assessmentId
        });
      }
    }
  };

  const handleVoiceNextQuestion = () => {
    // Guard against rapid spacebar (double-submit in same render frame)
    if (voiceAdvancingRef.current) return;
    if (!initialQuestions[currentIndex]) return;
    const currentQuestion = initialQuestions[currentIndex];

    const captured = voiceAssessment.captureAndAdvance(
      currentQuestion.id,
      currentQuestion.answer,
      questionStartTime
    );

    if (!captured) return;

    voiceAdvancingRef.current = true;

    // Immediate advance, no feedback flash
    setCurrentIndex(prev => prev + 1);
    setQuestionsAnswered(prev => prev + 1);
    setQuestionStartTime(Date.now());

    // Reset guard after React processes the state update
    requestAnimationFrame(() => {
      voiceAdvancingRef.current = false;
    });
  };

  // Update questionStartTime when moving to a new question
  useEffect(() => {
    if (initialQuestions[currentIndex]) {
      setQuestionStartTime(Date.now());
    } else if (currentIndex >= initialQuestions.length && !isProcessingEnd) {
      handlePracticeEnd();
    }
  }, [currentIndex]);

  if (!initialQuestions || initialQuestions.length === 0) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div>No questions available</div>
      </div>
    );
  }

  if (isProcessingEnd) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white p-6 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-md p-6 flex flex-col items-center gap-4">
          <div className="w-20 h-20 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          <div className="text-xl font-medium text-gray-600">
            {isVoiceMode && voiceAssessment.pendingCount > 0
              ? 'Processing answers...'
              : 'Processing final results...'}
          </div>
        </div>
      </div>
    );
  }

  if (isVoiceMode && voiceAssessment.micError) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white p-6 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-md p-8 max-w-md text-center space-y-4">
          <div className="text-red-500 text-5xl mb-2">🎤</div>
          <h2 className="text-xl font-bold text-gray-800">Microphone Error</h2>
          <p className="text-gray-600">{voiceAssessment.micError}</p>
          <div className="flex gap-3 justify-center pt-2">
            <button
              onClick={async () => {
                const success = await voiceAssessment.initializeMicrophone();
                if (success) {
                  testStartTime.current = Date.now();
                  setQuestionStartTime(Date.now());
                }
              }}
              className="px-6 py-3 bg-indigo-500 text-white rounded-lg font-medium hover:bg-indigo-600 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!initialQuestions[currentIndex]) {
    return null;
  }

  const currentQuestion = initialQuestions[currentIndex];

  const inputProps = {
    question: currentQuestion,
    onAnswer: handleAnswer,
    showFeedback,
    currentQuestionIndex: currentIndex,
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white p-6 flex items-center">
      <div className="max-w-4xl w-full mx-auto space-y-6">
        <TestTimer
          onComplete={handlePracticeEnd}
          isActive={!isSubmitting.current}
        />

        {/* Question Card */}
        <div className="bg-white rounded-lg shadow-md p-6">
          {isVoiceMode ? (
            <VoiceInput
              question={currentQuestion}
              onNextQuestion={handleVoiceNextQuestion}
              isRecording={voiceAssessment.isRecording}
            />
          ) : (
            <TextInput {...inputProps} />
          )}
        </div>
      </div>
    </div>
  );
};

export default QuestionScreen;